/**
 * EstimateSyncCard.tsx
 *
 * 見積書↔請求書 連携フロー（estimate-invoice-sync 設計書 §3.2）:
 * 「見積書は請求書の合算投影である」という業務モデルを可視化するカード。
 * CasePage（context="case-page"）と DocumentPage（context="invoice-page"）の両方から使う。
 *
 * 同期状態 (sync) の判定は API 側（GET /cases/:id/estimate/status）に一任し、
 * このコンポーネントは状態表示と「見積書を作成 / 開く」のみを担う。
 * 明細の同期は請求書の保存/取消時にサーバー側で自動実行される（手動更新ボタンは廃止）。
 */
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { casesApi } from "../../api.js";
import { Button } from "../ui/Button.js";
import { toast } from "../Toast.js";

type Props = {
  caseId: string;
  /** "invoice-page" = 請求書編集画面 / "case-page" = 案件画面 */
  context: "invoice-page" | "case-page";
  /** 遷移元の書類ID（請求書画面から見積書を開いた際、見積書側に「請求書に戻る」導線を出すために渡す） */
  fromDocumentId?: string;
};

// api.ts の request() が投げるエラーは status/body を持つ（{ error: { code, message, document_id? } }）
export type EstimateApiError = Error & {
  status?: number;
  body?: { error?: { code?: string; message?: string; document_id?: string } };
};

export function EstimateSyncCard({ caseId, context, fromDocumentId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const navState = fromDocumentId ? { state: { fromDocumentId } } : undefined;

  const { data: status } = useQuery({
    queryKey: ["case", caseId, "estimate-status"],
    queryFn: () => casesApi.estimateStatus(caseId),
    enabled: !!caseId,
  });

  const generateMut = useMutation({
    mutationFn: () => casesApi.estimateGenerate(caseId, "create"),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ["case", caseId] });
      qc.invalidateQueries({ queryKey: ["case", caseId, "estimate-status"] });
      qc.invalidateQueries({ queryKey: ["document", doc.id] });
      toast("見積書を作成しました");
    },
    onError: (e: EstimateApiError) => {
      // レース（他タブでの並行作成 等）でも表示を最新状態に戻す
      qc.invalidateQueries({ queryKey: ["case", caseId] });
      qc.invalidateQueries({ queryKey: ["case", caseId, "estimate-status"] });
      if (e.body?.error?.code === "ESTIMATE_DRAFT_EXISTS") {
        toast("既存の下書きがあります。表示を最新の状態に更新しました。", "error");
      } else {
        toast(e.message, "error");
      }
    },
  });

  if (!status || status.sync === "no_invoices") return null;

  const openEstimate = () => status.estimate && navigate(`/documents/${status.estimate.id}`, navState);

  return (
    <div style={{ marginTop: context === "invoice-page" ? 16 : 0 }}>
      {status.sync === "not_created" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
          <span style={{ fontSize: 13, color: "#475569" }}>
            この案件の見積書はまだ作成されていません。請求書の合算から見積書を作れます。
          </span>
          <Button variant="primary" disabled={generateMut.isPending} onClick={() => generateMut.mutate()}>
            {generateMut.isPending ? "作成中…" : "見積書を作成"}
          </Button>
        </div>
      )}

      {status.sync === "stale" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderRadius: 10, border: "1px solid #fcd34d", background: "#fffbeb" }}>
          <span style={{ fontSize: 13, color: "#92400e" }}>
            ⚠ 見積書が請求書の内容と一致していません。請求書を保存すると自動で合算内容に更新されます。
          </span>
          <Button variant="outline" onClick={openEstimate}>見積書を開く</Button>
        </div>
      )}

      {status.sync === "in_sync" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderRadius: 10, border: "1px solid #99f6e4", background: "#f0fdfa" }}>
          <span style={{ fontSize: 13, color: "#134e4a" }}>
            ✓ 見積書は請求書の内容と一致しています。
          </span>
          <Button variant="outline" onClick={openEstimate}>見積書を開く</Button>
        </div>
      )}

      {status.sync === "issued" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
          <span style={{ fontSize: 13, color: "#475569" }}>
            見積書 v{status.estimate?.version} は発行済みです。内容を変えるには見積書画面から修正版を作成してください。
          </span>
          <Button variant="outline" onClick={openEstimate}>見積書を開く</Button>
        </div>
      )}

    </div>
  );
}
